import type { TraceEntry } from '../audio/trace';

export interface EntryDuty {
  wallSec: number;
  audioSec: number;
  dutyPct: number;
}

/**
 * Compare adjacent trace entries without adding redundant fields to the stored trace.
 *
 * `wallMs` is the basis because it continues across OS sleep; `perfMs` can restart on a
 * replacement page, as the Android trace did. A civil-clock adjustment can still make a
 * wall delta invalid, so non-positive spans are omitted. Sub-second disagreement is also
 * omitted: it is ordinary sampling jitter, not the 51% duty failure this readout exists
 * to expose, and whole seconds/percent avoid overstating either clock's precision.
 */
export function entryDuty(previous: TraceEntry | undefined, entry: TraceEntry): EntryDuty | null {
  if (!previous
      || !Number.isFinite(previous.wallMs)
      || !Number.isFinite(entry.wallMs)
      || previous.ctxSec === null
      || entry.ctxSec === null
      || !Number.isFinite(previous.ctxSec)
      || !Number.isFinite(entry.ctxSec)) return null;

  const wallSec = (entry.wallMs - previous.wallMs) / 1000;
  const audioSec = entry.ctxSec - previous.ctxSec;
  if (wallSec <= 0 || audioSec < 0 || Math.abs(wallSec - audioSec) < 1) return null;
  return {
    wallSec: Math.round(wallSec),
    audioSec: Math.round(audioSec),
    dutyPct: Math.round((audioSec / wallSec) * 100),
  };
}
